import type { PlanDto } from '@mg/shared';

/**
 * What a plan's price reads as on screen.
 *
 * ## The bug this fixes, and why it reached two pages
 *
 * Both the Billing page and Billing Admin wrote their own version of
 *
 * ```ts
 * plan.monthlyPriceUsd > 0 ? formatPlanPrice(plan.monthlyPriceUsd) : '—'
 * ```
 *
 * which renders a **$0 plan as a dash**. The first live plan on the RescueMeds install is a $0
 * partner discount, so the price column simply showed "—" on both pages and the amount looked
 * missing rather than free.
 *
 * `> 0` was standing in for a different question. The dash is there for a plan that has **no price
 * from Stripe yet** — `stripePriceId` is null until an admin syncs plans, and such a plan cannot be
 * selected at all. A zero-amount price is the opposite: fully configured, selectable, and worth
 * exactly nothing per user. Conflating "no price" with "a price of zero" is what produced the dash.
 *
 * So the test is `stripePriceId`, which is what "unsynced" means everywhere else on both pages
 * (they already compute `const unsynced = !plan.stripePriceId` for the button state — the price
 * column was the one place that asked the amount instead).
 *
 * It lives here, shared, because the duplication is how one wrong guard became two.
 */

/** `$300`, `$0` — whole dollars, which is how a per-user plan price is quoted. */
export function formatPlanPrice(usd: number): string {
  return usd.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  });
}

/** `/ user / month` or `/ user / year`. */
export function planIntervalSuffix(plan: Pick<PlanDto, 'billingInterval'>): string {
  return `/ user / ${plan.billingInterval === 'YEAR' ? 'year' : 'month'}`;
}

/**
 * The price cell: the amount when Stripe has given this plan a price (**including $0**), and an
 * em dash only when it has not.
 */
export function planPriceLabel(plan: Pick<PlanDto, 'monthlyPriceUsd' | 'stripePriceId'>): string {
  return plan.stripePriceId ? formatPlanPrice(plan.monthlyPriceUsd) : '—';
}

/** The same, with the per-user interval appended — the Billing Admin table's single cell. */
export function planPriceWithInterval(
  plan: Pick<PlanDto, 'monthlyPriceUsd' | 'stripePriceId' | 'billingInterval'>,
): string {
  if (!plan.stripePriceId) return '—';
  return `${formatPlanPrice(plan.monthlyPriceUsd)} ${planIntervalSuffix(plan)}`;
}
